import React, { ReactElement, useEffect, useState } from "react";
import Icon from "@mlb/react-patterns/components/Icon/icon";
import { IconSizes } from "@mlb/react-patterns/components/Icon/icon.types";

import { Context } from "../../../context";
import {
  Timeline,
  TickerElapsed,
  TickerTotal,
} from "../../components/Timeline/Timeline";
import CameraOptionsCycle from "../../components/CameraOptions/CameraOptionsCycle";
import { FieldVisionController } from "../../FieldVisionController";
import {
  PlaybackControlsWrapper,
  PlaybackButton,
  ExitFullscreenButton,
} from "../fieldvision.style";
import { useMedia } from "@mlb/react-patterns/utils/hooks";
import SettingsMenuPopover from "../SettingsMenu/SettingsMenuPopover";
import { UserOptionPreset } from "../../../util/user-options";
import SettingsMenu from "../SettingsMenu/SettingsMenu";

interface PlaybackControlsProps {
  containerRef: React.RefObject<HTMLElement>;
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  rewind: () => void;
  fastForward: () => void;
  onFullscreen: (enteringFullscreen: boolean) => void;
  onInfoButton: () => void;
  toggleLoop: () => void;
  addSubscriberToContextChange: (
    name: string,
    cb: (context: Context) => void
  ) => void;
  controller: FieldVisionController;
  isHidden: boolean;
}

function PlaybackControls(
  props: PlaybackControlsProps
): ReactElement<PlaybackControlsProps> {
  const {
    isPlaying,
    play,
    pause,
    addSubscriberToContextChange,
    controller,
    isHidden = true,
  } = props;
  const [currentlyPlaying, setCurrentlyPlaying] = useState(isPlaying);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loopOn, setLoopOn] = useState(true);
  const [currentSpeed, setCurrentSpeed] = useState(1);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [currentVisualQualityPreset, setCurrentVisualQualityPreset] = useState(
    "high" as UserOptionPreset
  );

  let menuDisplay = isSettingsMenuOpen ? "block" : "none";

  const isMobile = useMedia({ orientation: "portrait", maxWidth: "750px" });

  useEffect(() => {
    addSubscriberToContextChange("playbackControls", (context: Context) => {
      setCurrentlyPlaying(context.state.playing);
      setLoopOn(context.clock.loop);
      setIsFullscreen(context.state.isFullscreen);
    });

    addSubscriberToContextChange("settings", (context: Context) => {
      setCurrentSpeed(context.clock.speed);
      setCurrentVisualQualityPreset(context.userOptions.values.presetName);
    });

    // Handle when a user interacts with the broser Fullscreen APi (https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API)
    document.addEventListener("fullscreenchange", () => {
      if (document.fullscreenElement) {
        controller.enterFullscreen();
      } else {
        controller.exitFullscreen();
      }
    });
  }, []);

  const playButton = (
    <PlaybackButton
      id="fv-play-button"
      title={currentlyPlaying ? "pause" : "play"}
      onClick={() => {
        currentlyPlaying ? pause() : play();
        setCurrentlyPlaying(!currentlyPlaying);
      }}
    >
      <Icon
        name={currentlyPlaying ? "pause" : "play"}
        size={IconSizes.MD}
        fill="#fff"
      />
    </PlaybackButton>
  );

  const infoButton = (
    <PlaybackButton
      id="fv-info-button"
      title="info-fv"
      onClick={() => {
        props.onInfoButton();
      }}
    >
      <Icon name="info-fv" size={IconSizes.MD} fill="#fff" />
    </PlaybackButton>
  );

  const contextCb = (name: string, cb: (context: Context) => void) =>
    addSubscriberToContextChange(name, cb);

  const tickerElapsed = (
    <TickerElapsed
      addSubscriberToContextChange={contextCb}
      currentTime={controller.playCurrentTime}
      totalTime={controller.playTotalTime}
    />
  );

  const tickerTotal = (
    <TickerTotal
      addSubscriberToContextChange={contextCb}
      currentTime={controller.playCurrentTime}
      totalTime={controller.playTotalTime}
    />
  );

  const timeline = (
    <Timeline
      addSubscriberToContextChange={contextCb}
      currentTime={controller.playCurrentTime}
      totalTime={controller.playTotalTime}
      seekToTimelineClick={(time: number) => controller.seekToRelative(time)}
    />
  );

  const settingsButton = (
    <SettingsMenuPopover
      speed={controller.clock.speed}
      visualQualityPreset={controller.userOption.values.presetName}
      setPlaybackSpeed={controller.setPlaybackSpeed}
      setVisualQualityPreset={controller.setVisualQualityPreset}
      isSettingsMenuOpen={isSettingsMenuOpen}
      setIsSettingsMenuOpen={setIsSettingsMenuOpen}
    />
  );

  const mobileSettingsButton = (
    <div
      onClick={() => {
        isSettingsMenuOpen
          ? setIsSettingsMenuOpen(false)
          : setIsSettingsMenuOpen(true);
      }}
    >
      <Icon name="settings" size={IconSizes.MD} fill="#fff" />
    </div>
  );

  const compactCameraButton = <CameraOptionsCycle controller={controller} />;

  const fullscreenButton = (
    <PlaybackButton
      id="fv-fullscreen-button"
      title="full-screen"
      onClick={() => {
        props.onFullscreen(!isFullscreen);
        isFullscreen
          ? controller.exitFullscreen()
          : controller.enterFullscreen();
      }}
    >
      <Icon
        name={!isFullscreen ? "enter-fullscreen" : "exit-fullscreen"}
        size={IconSizes.MD}
        fill="#fff"
      />
    </PlaybackButton>
  );

  const exitFullscreenButton = (
    <ExitFullscreenButton
      onClick={() => {
        props.onFullscreen(false);
        controller.exitFullscreen();
      }}
    >
      <Icon name="close" size={IconSizes.MD} fill="#fff" />
    </ExitFullscreenButton>
  );

  return (
    <div
      id="fvodControls"
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
      hidden={isHidden}
    >
      <div>{isFullscreen ? exitFullscreenButton : null}</div>
      {!isMobile || (isMobile && !isFullscreen) ? (
        <PlaybackControlsWrapper>
          {playButton}
          {tickerElapsed}
          {timeline}
          {tickerTotal}
          {compactCameraButton}
          {isMobile && !isFullscreen ? null : settingsButton}
          {infoButton}
          {fullscreenButton}
        </PlaybackControlsWrapper>
      ) : (
        <div>
          <PlaybackControlsWrapper
            style={{ bottom: "100px", display: menuDisplay }}
            className="mobile"
          >
            <SettingsMenu
              speed={controller.clock.speed}
              visualQualityPreset={controller.userOption.values.presetName}
              setPlaybackSpeed={controller.setPlaybackSpeed}
              setVisualQualityPreset={controller.setVisualQualityPreset}
              setIsSettingsMenuOpen={setIsSettingsMenuOpen}
            />
          </PlaybackControlsWrapper>
          <PlaybackControlsWrapper className="mobile">
            <div className="row top">
              <div className="pair">
                {infoButton}
                {compactCameraButton}
                {mobileSettingsButton}
              </div>
            </div>
            <div className="row">
              {playButton}
              {tickerElapsed}
              {timeline}
              {tickerTotal}
              {fullscreenButton}
            </div>
          </PlaybackControlsWrapper>
        </div>
      )}
    </div>
  );
}

export default PlaybackControls;
